import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import AnalysisResultCard from 'components/AnalysisResultCard/AnalysisResultCard';
import Button from 'components/Button/Button';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import { BUTTON_VARIANT } from 'constants/interfaces';
import highlight from 'utils/highlight';

import type IAnalysisSidebarResultsTab from './interfaces/IAnalysisSidebarResultsTab';

import styles from './scss/AnalysisSidebarResultsTab.module.scss';

const ANALYSIS_RESULTS_SCROLLABLE_TARGET = 'analysis-sidebar-results-scroll';

function AnalysisSidebarResultsTab({
  valueChainMapAnalysisResults,
  isAnalysisResultsLoading,
  hasMoreAnalysisResults,
  isCopyAnalysisResultLoading,
  isDeleteAnalysisResultLoading,
  onBackToSetup,
  onLoadMoreAnalysisResults,
  onCopyAnalysisResult,
  onDeleteAnalysisResult,
  onViewResult,
  activeViewResultId,
  openResultId,
  openRequestId,
}: IAnalysisSidebarResultsTab): React.JSX.Element {
  const { t } = useTranslation();
  const hasAnalysisResults = valueChainMapAnalysisResults.length > 0;
  const shouldShowLoader = isAnalysisResultsLoading && !hasAnalysisResults;
  const shouldShowEmptyResults = !isAnalysisResultsLoading && !hasAnalysisResults;
  const resultNodesRef = useRef(new Map<number, HTMLDivElement>());
  // The openRequestId a result was last pointed out for, so further pages loading in later do
  // not re-trigger it, while a repeat "view details" on the same run does.
  const pointedOutForRequestRef = useRef<number | undefined>(undefined);

  // Retries as `valueChainMapAnalysisResults` grows: the target card may not be in the list yet
  // — pagination still loading, or the whole panel just opened and is fetching its first page —
  // so this keeps checking on every update instead of only on the request that set `openResultId`.
  useEffect(() => {
    if (openResultId == null || pointedOutForRequestRef.current === openRequestId) {
      return;
    }

    const node = resultNodesRef.current.get(openResultId);
    if (!node) {
      return;
    }

    node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    highlight(node);
    pointedOutForRequestRef.current = openRequestId;
  }, [openResultId, openRequestId, valueChainMapAnalysisResults]);

  return (
    <>
      <div
        id={ANALYSIS_RESULTS_SCROLLABLE_TARGET}
        className={styles.content}
      >
        {shouldShowLoader ? (
          <div className={styles.loaderWrapper} role="status" aria-live="polite">
            <SmallLoader size={30} />
          </div>
        ) : null}

        {shouldShowEmptyResults ? (
          <div className={styles.emptyResults}>
            <InfoIcon className={styles.emptyResultsIcon} aria-hidden="true" />
            <p className={styles.emptyResultsText}>
              {t('valueChainMap.noAnalysesYet')}
            </p>
          </div>
        ) : null}

        {hasAnalysisResults ? (
          <InfiniteScroll
            dataLength={valueChainMapAnalysisResults.length}
            next={onLoadMoreAnalysisResults}
            hasMore={hasMoreAnalysisResults}
            loader={null}
            scrollableTarget={ANALYSIS_RESULTS_SCROLLABLE_TARGET}
          >
            <div className={styles.resultsList}>
              {valueChainMapAnalysisResults.map((result) => (
                <div
                  key={result.id}
                  ref={(node) => {
                    if (node) {
                      resultNodesRef.current.set(result.id, node);
                    } else {
                      resultNodesRef.current.delete(result.id);
                    }
                  }}
                >
                  <AnalysisResultCard
                    result={result}
                    isDuplicateLoading={isCopyAnalysisResultLoading}
                    isDeleteLoading={isDeleteAnalysisResultLoading}
                    onDuplicate={() => onCopyAnalysisResult(result.id)}
                    onDelete={() => onDeleteAnalysisResult(result.id)}
                    onToggleVisibility={onViewResult ? () => onViewResult(result.id) : undefined}
                    isVisibilityActive={activeViewResultId === result.id}
                  />
                </div>
              ))}
            </div>
          </InfiniteScroll>
        ) : null}
      </div>

      <div className={styles.footer}>
        <Button
          title={t('valueChainMap.backToAnalysisSetup')}
          variant={BUTTON_VARIANT.cancel}
          className={styles.backToSetupButton}
          onClick={onBackToSetup}
        />
      </div>
    </>
  );
}

export default AnalysisSidebarResultsTab;
